// caiet UC51 — "Solicitări corectare dată expirare", drawn after the wireframe
// (wireframes/react/src/pages/SolicitariCorectare.tsx, read-only): the shared
// page head with "Solicitare nouă" beside the title, a zebra register whose
// medicine cell is the identity (name, then Formă · Doză · Cod · DCI) and whose
// "Creată" cell names who asked, and a read-only request dialog laid out as the
// owner specified (2026-09-15): the medicine with its two status tags and its
// details, as the movement dialog identifies one; the change as two labelled
// dates side by side (a third, "Data aprobată", when the administrator set a
// different one, caiet UC22-02); a MUD info-box saying what the status means;
// and, at the bottom, when, which entity, where the lot sits and who asked.
// The decision itself is the administrator's (caiet UC22), so there is nothing
// here to do but read and close.
//
// Kept although the wireframe does not draw them: the status filter (the one
// filter pattern every register carries, e3b7b70a) and the request's own
// comment, which the wireframe's form never asks for and this one does. Left
// out: the wireframe's "ID solicitare: SC-2026-0342" — the request has no human
// number, and its uuid is not something a screen prints (docs/ui-shape.md).
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudIcon,
  MudInfoBox,
  MudModal,
  MudSelect,
  MudTable,
  MudTag,
} from '@egov-moldova/mud-react';
import {
  listMyCorrections,
  type CorrectionStatus,
  type MyCorrectionRequest,
} from '../../../features/corrections/api';
import { QueryState } from '../../../components/QueryState/QueryState';
import { NewCorrectionRequestModal } from './NewCorrectionRequestModal';
import './corrections.css';
import {
  EMPTY_CELL,
  EmptyState,
  FilterBar,
  formatDate,
  formatDateTime,
  formatExpiry,
  MetadataLine,
  onModalDismiss,
  onRowOpen,
  PageHeader,
  type ActiveFilter,
} from '@simsm/ui-shared';
import { TableFooter, usePageSize } from '../../../components/TableFooter/TableFooter';
import { describeAuthorization, todayIso } from '../../../features/reporting/medicine-api';
import {
  authorisationStatus,
  medicineStateStatus,
  type StatusTagSpec,
} from '../../../features/reporting/medicine-identity';

interface TableRowClickDetail {
  row: { id?: string | number };
  index: number;
}

const STATUSES: CorrectionStatus[] = ['PENDING', 'APPROVED', 'REJECTED'];

// The name the row leads with, falling back to the nomenclator code when the
// catalogue no longer knows it — a code the reader can look up beats a name
// the system invented.
function medicineName(r: MyCorrectionRequest): string {
  return r.medicineName || r.medicineCode;
}

// The wireframe's status colours (SolicitariCorectare.tsx `ST`): "Spre aprobare"
// is accent, not brand — it is waiting on someone, not the product's own voice.
export function statusSemantic(s: CorrectionStatus): 'accent' | 'success' | 'danger' {
  if (s === 'APPROVED') return 'success';
  if (s === 'REJECTED') return 'danger';
  return 'accent';
}

function statusKey(s: CorrectionStatus): string {
  switch (s) {
    case 'PENDING':
      return 'operator_corrections.status.pending';
    case 'APPROVED':
      return 'operator_corrections.status.approved';
    case 'REJECTED':
      return 'operator_corrections.status.rejected';
  }
}

/** A labelled value on the dialog: the label's key, and the text under it. */
export interface LabelledValue {
  key: string;
  value: string;
}

// The medicine's two status tags, exactly as the movement dialog derives them
// (spec 054 T109; medicine-identity.ts): where the code stands on the national
// lists, then its state in SIMSM. `today` is passed in so a test can pin it.
export function medicineStatuses(r: MyCorrectionRequest, today: string): StatusTagSpec[] {
  return [
    authorisationStatus(
      { authorised: r.medicineAuthorised, authorisationExpiry: r.medicineAuthorisationExpiry },
      today,
    ),
    medicineStateStatus({ active: r.medicineActive }),
  ];
}

// The authorisation expiry line under the details, as the movement dialog
// says it (caiet UC50 step 4): "Autorizația expiră: …", or, once lapsed,
// "a expirat: … · poate fi raportat până la …" while UC20-10's grace period
// still holds the code active. Nothing for an unauthorised code.
export function authorisationNote(
  r: MyCorrectionRequest,
  today: string,
): { key: string; params: Record<string, string>; expired: boolean } | null {
  const note = describeAuthorization(
    {
      authorized: r.medicineAuthorised,
      authorizationExpiresOn: r.medicineAuthorisationExpiry ?? '',
    },
    today,
  );
  if (!note) return null;
  if (note.expired && r.medicineActive && r.medicineReportableUntil) {
    return {
      key: 'stock_reporting.dialog.search.authorisation_grace',
      params: { date: formatDate(note.iso), until: formatDate(r.medicineReportableUntil) },
      expired: true,
    };
  }
  return { key: note.key, params: { date: formatDate(note.iso) }, expired: note.expired };
}

// The change, as two labelled dates: the lot's date as reported, and the one
// the operator asked for. When the administrator approved a DIFFERENT date
// (caiet UC22-02) it stands as a third field, so the operator sees both what
// was asked and what was applied. Equal dates say nothing twice.
export function expiryFields(r: MyCorrectionRequest): LabelledValue[] {
  const fields: LabelledValue[] = [
    { key: 'operator_corrections.change.current', value: formatExpiry(r.currentExpiry) },
    { key: 'operator_corrections.change.proposed', value: formatExpiry(r.proposedExpiry) },
  ];
  if (r.status === 'APPROVED' && r.decidedExpiry && r.decidedExpiry !== r.proposedExpiry) {
    fields.push({
      key: 'operator_corrections.change.decided',
      value: formatExpiry(r.decidedExpiry),
    });
  }
  return fields;
}

// The shared formatDateTime says "Astăzi, 14:32" with MUD's capital, because a
// register cell stands alone. Under a label it reads mid-line, so the word is
// lowered, the way the report screen lowers it after "Început": the day is the
// only part with no digit in it, in every locale, so a month name keeps its
// capital and the clock is untouched.
export function midSentenceDateTime(iso: string): string {
  const [day, ...rest] = formatDateTime(iso).split(', ');
  const lowered = day && !/\d/.test(day) ? day.toLocaleLowerCase() : (day ?? '');
  return [lowered, ...rest].join(', ');
}

// The four facts at the bottom, labelled: when, which entity (by name, never
// its IDNO, except when the catalogue cannot name it, owner decision D5), where
// (the location the request was made from; on a request that predates it, the
// sites still holding the lot, or nothing), and who asked (rows written before
// specs/030-actor-names carry no name).
export function requestFacts(r: MyCorrectionRequest): LabelledValue[] {
  return [
    { key: 'operator_corrections.meta.requested_at', value: midSentenceDateTime(r.createdAtIso) },
    { key: 'operator_corrections.meta.entity', value: r.registrarName || r.registrarId },
    {
      key: 'operator_corrections.meta.location',
      value: r.siteName || r.lotLocations.join(' · ') || EMPTY_CELL,
    },
    { key: 'operator_corrections.meta.operator', value: r.operatorName || EMPTY_CELL },
  ];
}

// What the status means for the operator, in MUD's info-box voices. There is no
// green info-box ("there is no Success info-box at all", mud/components/
// messaging.md), so an approval is told in the info voice; the wireframe's green
// box is the one thing it drew that MUD does not have. The administrator's own
// words, when there are any, come back under the label they deserve: a rejection
// has a reason, an approval has at most a comment.
export interface Outcome {
  variant: 'info' | 'error';
  key: string;
  params: Record<string, string>;
  comment: string;
  commentKey: string;
}

export function outcome(r: MyCorrectionRequest): Outcome {
  if (r.status === 'APPROVED') {
    return {
      variant: 'info',
      key: 'operator_corrections.status_note.approved',
      params: { date: formatDate(r.decidedAtIso) },
      comment: r.decisionComment,
      commentKey: r.decisionComment ? 'operator_corrections.comment.decision' : '',
    };
  }
  if (r.status === 'REJECTED') {
    return {
      variant: 'error',
      key: 'operator_corrections.status_note.rejected',
      params: {},
      comment: r.decisionComment,
      commentKey: r.decisionComment ? 'operator_corrections.comment.rejection' : '',
    };
  }
  return {
    variant: 'info',
    key: 'operator_corrections.status_note.pending',
    params: {},
    comment: '',
    commentKey: '',
  };
}

export function OperatorCorrectionsPage(): JSX.Element {
  const { t } = useTranslate();
  // The criteria and the open request live in the address (ACCS-11/12/13), so a
  // filtered view can be linked to and the browser's Back returns to it.
  const [params, setParams] = useSearchParams();
  const status = STATUSES.find((s) => s === params.get('status')) ?? '';
  const page = Math.max(1, Number(params.get('page')) || 1);
  const activeId = params.get('request') ?? null;

  function setParam(key: string, value: string): void {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  }

  const [creating, setCreating] = useState(false);

  const query = useQuery({
    queryKey: ['operator-corrections'],
    queryFn: listMyCorrections,
    staleTime: 30_000,
  });

  const all = useMemo(() => query.data ?? [], [query.data]);
  const items = useMemo(
    () => (status ? all.filter((r) => r.status === status) : all),
    [all, status],
  );
  const [pageSize, setPageSize] = usePageSize('operator-corrections');
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const pageItems = useMemo(
    () =>
      items.slice(
        (Math.min(page, totalPages) - 1) * pageSize,
        Math.min(page, totalPages) * pageSize,
      ),
    [items, page, pageSize, totalPages],
  );

  const active = useMemo<MyCorrectionRequest | null>(
    () => all.find((r) => r.id === activeId) ?? null,
    [all, activeId],
  );
  // A `?request=` naming a request this operator does not have would otherwise
  // leave the address claiming a dialogue that is not open.
  useEffect(() => {
    if (activeId && !query.isLoading && !active) setParam('request', '');
  }, [activeId, active, query.isLoading]);

  // The wireframe's five columns: Medicament, Lot, Data de expirare, Creată, Statut.
  const columns = [
    { key: 'medicine', label: t('operator_corrections.column.medicine') },
    { key: 'lot', label: t('operator_corrections.column.lot') },
    { key: 'expiry', label: t('operator_corrections.column.expiry') },
    { key: 'createdAt', label: t('operator_corrections.column.sent') },
    { key: 'status', label: t('operator_corrections.column.status') },
  ];

  const tableRows = useMemo(
    () =>
      pageItems.map((r, i) => ({
        id: r.id || `r-${i}`,
      })),
    [pageItems],
  );

  const activeFilters: ActiveFilter[] = status
    ? [
        {
          key: 'status',
          label: t('operator_corrections.filter.status'),
          value: t(statusKey(status)),
          onRemove: () => setParam('status', ''),
        },
      ]
    : [];

  return (
    <section className="simsm-corrections">
      {/* caiet UC51-01: the dedicated menu is where a request is STARTED, not
          only where the history is read (finding A-04). */}
      <PageHeader
        title={t('operator_corrections.title')}
        lede={t('operator_corrections.lede')}
        actions={
          <MudButton shape="circular" variant="primary" size="md" onClick={() => setCreating(true)}>
            {t('operator_corrections.action.new')}
          </MudButton>
        }
      />

      <FilterBar
        active={activeFilters}
        onReset={() => setParam('status', '')}
        labels={{
          filters: t('common.filter.button'),
          reset: t('common.filter.reset'),
          panelAriaLabel: t('operator_corrections.filters.aria_label'),
          activeAriaLabel: t('common.filter.active_aria_label'),
        }}
        panel={
          <label className="simsm-filter-bar__field">
            <span className="simsm-filter-bar__field-label">
              {t('operator_corrections.filter.status')}
            </span>
            <MudSelect
              size="medium"
              value={status}
              aria-label={t('operator_corrections.filter.status')}
              onMudChange={(e: CustomEvent<{ value: string }>) =>
                setParam('status', e.detail.value)
              }
            >
              <option value="">{t('operator_corrections.filter.status_all')}</option>
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(statusKey(value))}
                </option>
              ))}
            </MudSelect>
          </label>
        }
      />

      {/* Loading, a failed fetch and a genuinely empty history are three
          different answers (audit A2). */}
      <QueryState
        query={query}
        isEmpty={items.length === 0}
        loadingText={t('operator_corrections.loading')}
        empty={
          /* The one register the wireframe deliberately does NOT keep a table
             for: "fără solicitări nu arătăm un tabel gol" (SolicitariCorectare.tsx:115). */
          <EmptyState
            icon="document"
            title={
              status ? t('operator_corrections.empty_filtered') : t('operator_corrections.empty')
            }
            body={
              status
                ? t('operator_corrections.empty_filtered_body')
                : t('operator_corrections.empty_body')
            }
          />
        }
      >
        <div className="simsm-corrections__register">
          <MudTable
            className="simsm-table"
            columns={columns}
            rows={tableRows}
            hoverable={true}
            rowStyle="zebra"
            aria-label={t('operator_corrections.title')}
            onMudRowClick={onRowOpen<TableRowClickDetail>((_index, { row }) => {
              if (row.id) setParam('request', String(row.id));
            })}
          >
            {pageItems.flatMap((r, idx) => {
              const rowId = r.id || `r-${idx}`;
              return [
                // The wireframe's compact identity: the name, then Formă · Doză ·
                // Cod · DCI beneath it. The name is a control because mud-table
                // rows take no focus, so the keyboard needs something to reach;
                // it is drawn as the name, not as a button.
                <span
                  key={`medicine-${rowId}`}
                  slot={`cell-medicine-${idx}`}
                  className="simsm-corrections__identity"
                >
                  <MudButton
                    className="simsm-corrections__identity-name"
                    shape="rectangular"
                    appearance="text"
                    variant="strict"
                    size="sm"
                    label={t('operator_corrections.row.open', {
                      medicine: medicineName(r),
                      lot: r.lot,
                    })}
                    onClick={() => setParam('request', rowId)}
                  >
                    {medicineName(r)}
                  </MudButton>
                  <MetadataLine
                    wrap
                    items={[
                      { label: t('operator_corrections.field.form'), value: r.medicineForm },
                      { label: t('operator_corrections.field.dose'), value: r.medicineDose },
                      { label: t('operator_corrections.field.code'), value: r.medicineCode },
                      {
                        label: t('operator_corrections.field.dci'),
                        value: r.medicineDci,
                        title: r.medicineDci || undefined,
                      },
                    ]}
                  />
                </span>,
                <span
                  key={`lot-${rowId}`}
                  slot={`cell-lot-${idx}`}
                  className="simsm-corrections__num"
                >
                  {r.lot}
                </span>,
                // Reported → decided (or proposed while undecided), with the
                // proposal beneath when the administrator changed it.
                <span
                  key={`expiry-${rowId}`}
                  slot={`cell-expiry-${idx}`}
                  className="simsm-corrections__expiry"
                >
                  <span>{formatExpiry(r.currentExpiry)}</span>
                  <MudIcon name="arrow-right" size={16} aria-hidden="true" />
                  <strong>{formatExpiry(r.decidedExpiry || r.proposedExpiry)}</strong>
                  {r.decidedExpiry && r.decidedExpiry !== r.proposedExpiry ? (
                    <span className="simsm-corrections__expiry-note">
                      {t('operator_corrections.cell.proposed_was', {
                        date: formatExpiry(r.proposedExpiry),
                      })}
                    </span>
                  ) : null}
                </span>,
                // "Creată": when, and beneath it who (the wireframe's MetadataLine).
                <span
                  key={`created-${rowId}`}
                  slot={`cell-createdAt-${idx}`}
                  className="simsm-corrections__created"
                >
                  <span>{formatDateTime(r.createdAtIso)}</span>
                  <MetadataLine
                    items={[
                      { label: t('operator_corrections.field.created_by'), value: r.operatorName },
                    ]}
                  />
                </span>,
                <span key={`status-${rowId}`} slot={`cell-status-${idx}`}>
                  <MudTag
                    size="sm"
                    semantic={statusSemantic(r.status)}
                    label={t(statusKey(r.status))}
                  />
                </span>,
              ];
            })}
          </MudTable>
        </div>
        <TableFooter
          total={items.length}
          page={Math.min(page, totalPages)}
          pageSize={pageSize}
          onPage={(next) => setParam('page', next > 1 ? String(next) : '')}
          onPageSize={setPageSize}
        />
      </QueryState>

      <NewCorrectionRequestModal open={creating} onClose={() => setCreating(false)} />

      {active ? (
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — seven AGE
        // components emit `mudClose` bubbling+composed, so a descendant closing inside this
        // modal reaches this handler too; the guard answers only a real dismissal.
        <MudModal
          open={true}
          size="md"
          closeLabel={t('common.close')}
          onMudClose={onModalDismiss(() => setParam('request', ''))}
        >
          {/* The wireframe's heading: the title, and the status beside it. */}
          <div slot="title" className="simsm-corrections__dialog-heading">
            <h2>{t('operator_corrections.modal.title')}</h2>
            <MudTag semantic={statusSemantic(active.status)} label={t(statusKey(active.status))} />
          </div>
          {/* The owner's layout (2026-09-15), four blocks 24px apart, 16px
              inside each: the medicine, the change, where it stands, the facts.
              Not boxed: the modal is the surface. */}
          <div className="simsm-corrections__dialog">
            {/* 1. The medicine, identified as the movement dialog identifies
                one: the name with its two status tags beside it, the labelled
                details beneath, and the authorisation expiry line. */}
            {(() => {
              const today = todayIso();
              const note = authorisationNote(active, today);
              return (
                <div className="simsm-correction-detail__medicine">
                  <div className="simsm-correction-detail__medicine-head">
                    <span className="simsm-correction-detail__medicine-name">
                      {medicineName(active)}
                    </span>
                    <span className="simsm-correction-detail__medicine-tags">
                      {medicineStatuses(active, today).map((status) => (
                        <MudTag
                          key={status.key}
                          variant="status"
                          size="sm"
                          semantic={status.semantic}
                          label={t(status.key)}
                        />
                      ))}
                    </span>
                  </div>
                  <MetadataLine
                    wrap
                    items={[
                      { label: t('operator_corrections.field.form'), value: active.medicineForm },
                      { label: t('operator_corrections.field.dose'), value: active.medicineDose },
                      { label: t('operator_corrections.field.code'), value: active.medicineCode },
                      { label: t('operator_corrections.field.dci'), value: active.medicineDci },
                      {
                        label: t('operator_corrections.field.producer'),
                        value: active.medicineProducer,
                      },
                      { label: t('operator_corrections.field.lot'), value: active.lot },
                    ]}
                  />
                  {note ? (
                    <span
                      className={
                        note.expired
                          ? 'simsm-correction-detail__authorisation simsm-correction-detail__authorisation--expired'
                          : 'simsm-correction-detail__authorisation'
                      }
                    >
                      {t(note.key, note.params)}
                    </span>
                  ) : null}
                </div>
              );
            })()}

            {/* 2. The change: the current date and the corrected one, side by
                side under their labels; a third field when the administrator
                approved a different date. Below it, quietly, the operator's
                own words when there were any. */}
            {(() => {
              const fields = expiryFields(active);
              return (
                <div className="simsm-correction-detail__change">
                  <dl
                    className="simsm-correction-detail__fields"
                    role="group"
                    aria-label={t('operator_corrections.change.aria', {
                      from: fields[0]?.value ?? '',
                      to: fields.at(-1)?.value ?? '',
                    })}
                  >
                    {fields.map((field) => (
                      <div key={field.key} className="simsm-correction-detail__field">
                        <dt>{t(field.key)}</dt>
                        <dd>{field.value}</dd>
                      </div>
                    ))}
                  </dl>
                  {active.requestComment ? (
                    <p className="simsm-correction-detail__request-comment">
                      <span className="simsm-correction-detail__label">
                        {t('operator_corrections.comment.request')}
                      </span>
                      {active.requestComment}
                    </p>
                  ) : null}
                </div>
              );
            })()}

            {/* 3. Where it stands: the tag beside the title says the word, this
                says what it means for the operator. A rejection leads with the
                administrator's reason; an approval keeps their comment after
                the fact. */}
            {(() => {
              const o = outcome(active);
              return (
                <MudInfoBox
                  variant={o.variant}
                  emphasis="subtle"
                  {...(o.variant === 'error' && o.comment ? { titleText: t(o.commentKey) } : {})}
                >
                  {o.variant === 'error' && o.comment ? (
                    <p className="simsm-correction-detail__reason">{o.comment}</p>
                  ) : null}
                  <p className="simsm-correction-detail__outcome">{t(o.key, o.params)}</p>
                  {o.variant !== 'error' && o.comment ? (
                    <p className="simsm-correction-detail__decision">
                      <strong>{t(o.commentKey)}:</strong> {o.comment}
                    </p>
                  ) : null}
                </MudInfoBox>
              );
            })()}

            {/* 4. The facts, labelled, four across (two by two when narrow):
                when, which entity, where the lot sits, who asked. */}
            <dl className="simsm-correction-detail__facts">
              {requestFacts(active).map((fact) => (
                <div key={fact.key} className="simsm-correction-detail__fact">
                  <dt>{t(fact.key)}</dt>
                  <dd>{fact.value}</dd>
                </div>
              ))}
            </dl>
          </div>
          <div slot="actions" className="simsm-corrections__footer">
            <MudButton
              shape="circular"
              appearance="outlined"
              variant="strict"
              size="md"
              onClick={() => setParam('request', '')}
            >
              {t('operator_corrections.action.close')}
            </MudButton>
          </div>
        </MudModal>
      ) : null}
    </section>
  );
}
